import React, { useState, useCallback, useEffect } from "react";
import { toast } from "react-hot-toast";
import userServices from "../../api/services/userService";
import { confirmDanger } from "../../utils/confirmDialog";
import {
  FilterSection,
  ActiveFilterPills,
  UserProfilesTable,
  UserProfilesPagination,
  EditUserProfileModal,
  CreateAppUserModal,
  useUserProfiles,
} from "../../components/UserProfiles";
import { getAdminPanelRole, canEditAppUserProfile } from "../../utils/adminPanelRole";

const PROFILE_URL = "https://naseebi.com/en/home/profiledetail";

export default function UserProfiles() {
  const {
    filters,
    handleFilterChange,
    clearFilters,
    removeFilter,
    getFilterLabel,
    getActiveFiltersCount,
    activeFilterEntries,
    primaryFields,
    secondaryFields,
    users,
    total,
    isLoading,
    backendPage,
    setBackendPage,
    backendLimit,
    fetchFilteredUsers,
  } = useUserProfiles();

  const [editUserId, setEditUserId] = useState(null);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const adminRoleKey = getAdminPanelRole();

  /** Create app user: Admin + Developer only (CSR uses edit only). */
  const canCreateAppUser = adminRoleKey === "admin" || adminRoleKey === "developer";

  /** Edit app user profile: Admin, Developer, CSR, CSR Team Lead. */
  const canEditUserProfile = canEditAppUserProfile(adminRoleKey);

  const canDeleteAppUser = adminRoleKey === "developer" || adminRoleKey === "admin";

  useEffect(() => {
    setSelectedIds([]);
  }, [backendPage]);

  const toggleSelectId = useCallback((id) => {
    if (id == null) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const toggleSelectAllOnPage = useCallback(() => {
    const pageIds = (users || []).map((u) => u.id).filter(Boolean);
    if (!pageIds.length) return;
    const allSelected = pageIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
    }
  }, [users, selectedIds]);

  const handleApply = () => {
    setBackendPage(1);
  };

  const handleViewProfile = (id) => {
    window.open(`${PROFILE_URL}/${id}`, "_blank");
  };

  const handleEditUser = useCallback((id) => {
    setEditUserId(id);
  }, []);

  const handleEditClose = useCallback(() => setEditUserId(null), []);

  const handleUserSaved = useCallback(() => {
    return fetchFilteredUsers(filters, backendPage, { refresh: true });
  }, [fetchFilteredUsers, filters, backendPage]);

  const handleDeleteUser = useCallback(
    async (userRow) => {
      const id = userRow?.id;
      if (!id) return;
      const name = `${userRow.firstName || ""} ${userRow.lastName || ""}`.trim() || `User #${id}`;
      const ok = await confirmDanger(
        `Permanently delete ${name} (ID ${id})? This removes the account and related data and cannot be undone.`,
        "Delete app user?"
      );
      if (!ok) return;
      setDeletingId(id);
      try {
        const res = await userServices.deleteAppUserByDeveloper(id);
        if (res?.success) {
          toast.success(res?.message || "User deleted");
          setSelectedIds((prev) => prev.filter((x) => x !== id));
          fetchFilteredUsers(filters, backendPage, { refresh: true });
        } else {
          toast.error(res?.message || "Delete failed");
        }
      } catch (err) {
        const msg =
          (err && typeof err === "object" && err.message) ||
          (typeof err === "string" ? err : null) ||
          "Failed to delete user";
        toast.error(msg);
      } finally {
        setDeletingId(null);
      }
    },
    [fetchFilteredUsers, filters, backendPage]
  );

  const handleBulkDeleteUsers = useCallback(async () => {
    if (!selectedIds.length) return;
    const ok = await confirmDanger(
      `Permanently delete ${selectedIds.length} selected user(s)? This cannot be undone.`,
      "Delete selected users?"
    );
    if (!ok) return;
    setBulkDeleting(true);
    try {
      const res = await userServices.bulkDeleteAppUsers(selectedIds);
      if (res?.success) {
        toast.success(res?.message || "Users deleted");
        setSelectedIds([]);
        fetchFilteredUsers(filters, backendPage, { refresh: true });
      } else {
        toast.error(res?.message || "Bulk delete failed");
      }
    } catch (err) {
      const msg =
        typeof err === "string"
          ? err
          : err && typeof err === "object"
            ? err.message
            : null;
      toast.error(msg || "Failed to delete users");
    } finally {
      setBulkDeleting(false);
    }
  }, [selectedIds, fetchFilteredUsers, filters, backendPage]);

  return (
    <div className="p-4 w-full">
      <div className="bg-white rounded-xl shadow-xl p-6">
        <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
          <h3 className="text-md md:text-xl font-bold text-gray-800">
            User Profiles
          </h3>
          <div className="flex items-center gap-2">
            {canCreateAppUser && (
              <button
                type="button"
                onClick={() => setCreateUserOpen(true)}
                className="text-xs md:text-sm font-semibold px-3 py-1.5 rounded-lg bg-red-500 text-white hover:bg-red-600">
                Create user
              </button>
            )}
            {getActiveFiltersCount() > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs md:text-sm text-red-600 hover:text-red-700 font-medium">
                Clear All Filters
              </button>
            )}
          </div>
        </div>

        <ActiveFilterPills
          activeKeys={activeFilterEntries}
          filters={filters}
          getLabel={getFilterLabel}
          onRemove={removeFilter}
        />

        <FilterSection
          primaryFields={primaryFields}
          secondaryFields={secondaryFields}
          values={filters}
          onChange={handleFilterChange}
          onApply={() => {
            setSelectedIds([]);
            handleApply();
          }}
          onClear={clearFilters}
          applyLabel="Apply"
          clearLabel="Clear All"
        />

        {canDeleteAppUser && !isLoading && users.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <button
              type="button"
              disabled={!selectedIds.length || bulkDeleting}
              onClick={handleBulkDeleteUsers}
              className="text-xs md:text-sm font-semibold px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {bulkDeleting ? "Deleting…" : `Delete selected (${selectedIds.length})`}
            </button>
            {selectedIds.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                className="text-xs md:text-sm text-gray-600 hover:text-gray-900 font-medium">
                Clear selection
              </button>
            )}
          </div>
        )}

        <UserProfilesTable
          users={users}
          page={backendPage}
          limit={backendLimit}
          isLoading={isLoading}
          activeFiltersCount={getActiveFiltersCount()}
          onViewProfile={handleViewProfile}
          onEditUser={canEditUserProfile ? handleEditUser : undefined}
          onDeleteUser={canDeleteAppUser ? handleDeleteUser : undefined}
          deletingUserId={deletingId}
          showRowSelection={canDeleteAppUser}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelectId}
          onToggleSelectAll={toggleSelectAllOnPage}
          bulkDeleting={bulkDeleting}
        />

        <EditUserProfileModal
          userId={editUserId}
          isOpen={editUserId != null}
          onClose={handleEditClose}
          onSaved={handleUserSaved}
        />

        <CreateAppUserModal
          isOpen={createUserOpen}
          onClose={() => setCreateUserOpen(false)}
          onCreated={() => {
            setBackendPage(1);
            return fetchFilteredUsers(filters, 1, { refresh: true });
          }}
        />

        {total > 0 && (
          <UserProfilesPagination
            page={backendPage}
            total={total}
            limit={backendLimit}
            onPageChange={setBackendPage}
          />
        )}
      </div>
    </div>
  );
}
